import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';
import {
    fullscreenChange,
    getAddedNodesIterator,
    getFullscreenElement,
    getScrollHeight,
    getScrollLeft,
    getScrollTop,
    getScrollWidth,
} from 'app/core/helpers/dom';
import {randomUUIDGenerator} from 'app/core/helpers/uuid';
import {Constructor} from 'app/core/interfaces/common.interface';
import {
    Coordinates2D,
    OverlayComponentInputs,
    OverlayComponentOutputs,
    OverlayHideRequest,
    OverlayRenderRequest,
    OverlayRequestCallback,
    PositionDirection,
} from 'app/core/interfaces/overlay.interface';
import {DomService} from 'app/core/services/dom.service';
import {OverlayService} from 'app/core/services/overlay.service';
import {fromEvent, Subscription} from 'rxjs';
import {OverlayElementEditor} from '../../classes/overlay-element-editor';

interface RenderItem {
    component: Constructor<any>;
    inputs: OverlayComponentInputs;
    outputs: OverlayComponentOutputs;
    callback: OverlayRequestCallback;
    id: string;
    element?: HTMLElement;
}

@Component({
    selector: 'overlay-outlet',
    templateUrl: './overlay-outlet.component.html',
    styleUrls: ['./overlay-outlet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class OverlayOutletComponent implements OnInit, OnDestroy {
    @Input() template: TemplateRef<any>;
    @Input() shown: boolean;
    @Input() visible: boolean;

    @HostBinding('class.shown') get isShown() {
        return !!this.templates.size;
    }

    templates = new Map<string, RenderItem>();

    private subscriptions = new Map<string, Subscription>();
    private elements = new Map<string, HTMLElement>();
    private fakeFullscreen: Element;

    constructor(
        private $el: ElementRef<HTMLElement>,
        private overlayService: OverlayService,
        private domService: DomService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.subscriptions.set(
            'fullscreen',
            fullscreenChange(document.body).subscribe(() => this.onFullscreen())
        );
        this.subscriptions.set(
            'render',
            this.overlayService.render$.subscribe(request => this.onRender(request))
        );
        this.subscriptions.set(
            'hide',
            this.overlayService.hide$.subscribe(request => this.onHide(request))
        );
        this.subscriptions.set(
            'inputs',
            this.overlayService.inputs$.subscribe(request => this.onInputs(request))
        );
        this.subscriptions.set(
            'inputs',
            this.overlayService.overlayEditor$.subscribe(request => this.onOverlayEditor(request))
        );
        this.subscriptions.set(
            'mutation',
            this.domService
                .mutationObserver(this.$el.nativeElement, {
                    childList: true,
                })
                .subscribe(entries => this.afterRender(entries))
        );
    }

    ngOnDestroy() {
        for (let subscription of this.subscriptions.values()) {
            subscription.unsubscribe();
        }
        this.templates.clear();
        this.elements.clear();
    }

    onFullscreen() {
        requestAnimationFrame(() => {
            let state = document.body === getFullscreenElement();
            let fakeFullscreen = document.querySelector(`[data-fullscreen=${state}]`);
            this.$el.nativeElement.style.height = state ? `${fakeFullscreen.scrollHeight}px` : '';
            if (state) {
                this.fakeFullscreen = fakeFullscreen;
                this.subscriptions.set(
                    'scroll',
                    fromEvent(fakeFullscreen, 'scroll').subscribe(() => {
                        this.$el.nativeElement.style.transform = `translate3d(-${this.fakeScrollLeft}px, -${this.fakeScrollTop}px, 0)`;
                    })
                );
            } else {
                this.fakeFullscreen = null;
                this.$el.nativeElement.style.removeProperty('transform');
                if (this.subscriptions.has('scroll')) {
                    this.subscriptions.get('scroll').unsubscribe();
                    this.subscriptions.delete('scroll');
                }
            }
        });
    }

    onRender({component, inputs, outputs, callback}: OverlayRenderRequest) {
        let id = randomUUIDGenerator() as string;
        this.templates.set(id, {
            id,
            component,
            inputs,
            outputs,
            callback,
        });
        this.cd.detectChanges();
        this.cd.markForCheck();
    }

    afterRender(mutations: Array<MutationRecord>) {
        for (let addedNode of getAddedNodesIterator(mutations)) {
            let id: string;
            if (addedNode instanceof HTMLElement && (id = addedNode.dataset.id)) {
                let renderItem = this.templates.get(id);
                let editor = new OverlayElementEditor(id, addedNode, this.createShowFunction(), this.createRemoveFunction());
                this.elements.set(id, addedNode);
                renderItem.callback(editor);
            }
        }
    }

    onHide({id, callback}: OverlayHideRequest) {
        let element = this.elements.get(id);
        let editor = new OverlayElementEditor(id, element, this.createShowFunction(), this.createRemoveFunction());
        callback(editor);
    }

    onInputs({id, inputs}: {id: string; inputs: OverlayComponentInputs}) {
        this.templates.get(id).inputs = inputs;
        this.cd.detectChanges();
        this.cd.markForCheck();
    }

    onOverlayEditor({id, callback}: {id: string; callback: OverlayRequestCallback}) {
        let element = this.elements.get(id);
        callback(new OverlayElementEditor(id, element, this.createShowFunction(), this.createRemoveFunction()));
    }

    removeItem(id: string) {
        if (this.elements.has(id) || this.templates.has(id)) {
            this.elements.delete(id);
            this.templates.delete(id);
            this.cd.detectChanges();
        }
    }

    trackById(index, value: RenderItem) {
        return value.id;
    }

    private createShowFunction(): (element: HTMLElement, position: {coordinates: Coordinates2D; direction: PositionDirection}) => void {
        return (element, {coordinates, direction}, fixed = false, zIndex: string = null) => {
            if (coordinates) {
                let {x, y} = coordinates;
                let scrollTop = fixed ? this.fakeScrollTop : this.scrollTop;
                let scrollLeft = fixed ? this.fakeScrollLeft : this.scrollLeft;
                let scrollHeight = fixed ? window.innerHeight : this.scrollHeight;
                let scrollWidth = fixed ? window.innerWidth : this.scrollWidth;
                let verticalProperty = direction.vertical === 'down' ? 'top' : 'bottom';
                let horizontalProperty = direction.horizontal === 'right' ? 'left' : 'right';
                let verticalValue = verticalProperty === 'top' ? y + scrollTop : scrollHeight - y - scrollTop;
                let horizontalValue = horizontalProperty === 'left' ? x + scrollLeft : scrollWidth - x - scrollLeft;

                element.style[verticalProperty] = `${Math.round(verticalValue)}px`;
                element.style[horizontalProperty] = `${Math.round(horizontalValue)}px`;
            }
            element.classList.add('visible');
            element.classList.toggle('fixed', fixed);

            if (!element.parentElement) {
                return;
            }

            if (zIndex) {
                element.parentElement.style.zIndex = zIndex;
            } else {
                element.parentElement.style.removeProperty('z-index');
            }
        };
    }

    private createRemoveFunction(): (id: string) => void {
        return id => this.removeItem(id);
    }

    get fakeScrollTop() {
        return (this.fakeFullscreen && this.fakeFullscreen.scrollTop) || 0;
    }

    get fakeScrollLeft() {
        return (this.fakeFullscreen && this.fakeFullscreen.scrollLeft) || 0;
    }

    get scrollTop(): number {
        return this.fakeScrollTop || getScrollTop();
    }

    get scrollHeight(): number {
        return getScrollHeight();
    }

    get scrollLeft(): number {
        return this.fakeScrollLeft || getScrollLeft();
    }

    get scrollWidth(): number {
        return getScrollWidth();
    }
}
